import React, { useContext, useEffect, useState } from "react";

import cx from "classnames";
import { QuoteSettingContainerType } from "../../../../../common/interfaces/settings/QuoteSettingContainerType";
import {
    ClientProductTypes,
    isClientProductType
} from "../../../../../common/interfaces/enums/ProductType";
import Icon from "../../../../../common/components/Icon/Icon";
import ToolTip from "../../../../../common/components/Tooltips/Tooltip";
import { useHistory } from "react-router-dom";
import useIsMounted from "../../../../../common/hooks/useIsMounted";
import FamilySettingModal from "../../../Modals/FamilySettingsModal/FamilySettingsModal";
import Button from "../../../../../common/components/Forms/Button/Button";
import useAPI from "../../../../hooks/useAPI";
import { QuoteGroupContext } from "../../../../providers/RootContextProvider";
import QuoteSettingsEditor, {
    IProductEditorState,
    ProductSelectionState
} from "../QuoteSettingsEditor/QuoteSettingsEditor";
import { getMultiQuoteResultAPI } from "../../../../api/endpoints/ApiQuotes";
import useErrorLogger from "../../../../../common/hooks/useErrorLogger";
import PromoteDAP from "../../../../../digital-advice/components/PromoteDAP/PromoteDAP";
import DapWarningDialog from "../../../../../digital-advice/components/Modals/DapWarningDialog";

interface IQuoteSettingSidebarProps {
    settings: QuoteSettingContainerType[];
    isLoading: boolean;
    className?: string;
}

const QuoteSettingSidebar: React.FC<IQuoteSettingSidebarProps> = (props) => {
    const { settings, isLoading, className } = props;

    const history = useHistory();
    const isMounted = useIsMounted();
    const { setProductSelected } = useAPI();
    const { actions, selectors } = useContext(QuoteGroupContext);
    const { Log } = useErrorLogger();
    const search = new URLSearchParams(location.search);
    const [isFromDAP, setIsFromDAP] = useState(false);

    const getProductState = (): ProductSelectionState => {
        const state: ProductSelectionState = {};

        settings.forEach(
            (s) =>
                (state[s.productType] = {
                    hasSettings: s.caseId > 0,
                    isSelected:
                        isClientProductType(s.productType) &&
                        selectors.isProductEnabled(s.productType),
                    hasChanged: false
                })
        );

        return state;
    };

    const [isOpenFamilySettings, setIsOpenFamilySettings] = useState(false);
    const [productState, setProductState] = useState<ProductSelectionState>({});
    const [isApplyingChanges, setIsApplyingChanges] = useState(false);
    const [isOpenWarningDialogModal, setIsOpenWarningDialogModal] = useState(false);
    const [warningNextStepUrl, setWarningNextStepUrl] = useState("");

    const hasChangedSelectedProducts = Object.values(productState).some(
        (s) => s?.hasChanged
    );

    const hasSelectedProducts = Object.values(productState).some((s) => s?.isSelected);

    const businessTooltipContent = "Protect your business from loss of income when disaster strikes";

    const handleWarningDialogModal = () => {
        const isQuoteWarningConfirm =
            !!window.localStorage.getItem("quote-modified-warning") || false;
        if (!isQuoteWarningConfirm) {
            setIsOpenWarningDialogModal(true);
        } else {
            history.push(warningNextStepUrl);
        }
    };

    useEffect(() => {
        setIsFromDAP(search.has("da"));
        if (!isLoading) {
            setProductState(getProductState());
        }
    }, [isLoading]);

    return (
        <div
            className={cx(
                "layout-flex-column",
                "layout-hidden-tablet",
                "gap-pre-l",
                "space-pre-l"
            )}>
            <FamilySettingModal
                isOpen={isOpenFamilySettings}
                onSubmit={() => {
                    history.push("/products");
                }}
                onClose={() => {
                    isMounted.current && setIsOpenFamilySettings(false);
                }}
            />
            <DapWarningDialog
                isOpen={isOpenWarningDialogModal}
                onClose={(isContinue: boolean) => {
                    if (isContinue) history.push(warningNextStepUrl);
                    isMounted.current && setIsOpenWarningDialogModal(false);
                }}
            />
            <QuoteSettingsEditor
                title="Add-ons"
                productState={productState}
                setProductState={setProductState}
                className={className}
                isLoading={isLoading || isApplyingChanges}
                hasSelectedProducts={hasSelectedProducts}
                isFromDAP={isFromDAP}
                showWarning={handleWarningDialogModal}
                setWarningNextStepUrl={setWarningNextStepUrl}
            />

            {(hasChangedSelectedProducts || isApplyingChanges) && (
                <div className="gap-l layout-flex-column--centered">
                    <Button
                        component="button"
                        className="gap-m"
                        type="button"
                        variant="calltoaction"
                        isDisabled={!hasSelectedProducts}
                        size="s"
                        dataAttributes={{
                            "data-ga_category": "quote-form",
                            "data-ga_action": "sidebar",
                            "data-ga_label": "apply-button"
                        }}
                        onClick={async () => {
                            actions.setIsLoadingQuoteResults(true);
                            setIsApplyingChanges(true);
                            const productsToUpdate: Promise<void>[] = Object.keys(
                                productState
                            )
                                .filter((s) => productState[s]?.hasChanged)
                                .map(async (s: ClientProductTypes) =>
                                    setProductSelected(s, productState[s]?.isSelected)
                                );

                            await Promise.all(productsToUpdate)
                                .then(async () => {
                                    actions.clearQuoteResults();

                                    return getMultiQuoteResultAPI(
                                        selectors.getGroupId(),
                                        selectors.getPaymentFrequency()
                                    ).then((cards) => {
                                        actions.setQuoteResults(cards);
                                    });
                                })
                                .catch((e) => {
                                    Log(e, {
                                        source: "settings-sidebar--updated-products"
                                    });
                                })
                                .finally(() => {
                                    setIsApplyingChanges(false);
                                    actions.setIsLoadingQuoteResults(false);
                                });
                        }}>
                        Apply Changes
                    </Button>

                    {!isApplyingChanges && (
                        <a onClick={() => setProductState(getProductState())}>Cancel</a>
                    )}
                </div>
            )}
            <a
                href="https://businessinsurance.lifedirect.co.nz/OccupationSelection" //TODO: pass this through from config
                target="_blank"
                className={cx(
                    "panel-basic",
                    "panel-product-selection",
                    "layout-flex-row--justify-spaced",
                    "layout-flex-row--centered",
                    "business",
                    "gap-l",
                    {
                        ["cursor-pointer"]: !isLoading,
                        ["fade-light"]: isLoading
                    }
                )}>
                <label
                    className={cx(
                        "text-font-size-ml",
                        "gap-vertical-none",
                        "text-primary",
                        "layout-flex-row--centered")}>
                    Business Cover
                    <Icon id="ExternalLinkDark" size="xl" className="space-pre-m" />
                </label>
                <ToolTip tooltipContent={businessTooltipContent} />
            </a>
            {!isLoading && (
                <React.Fragment>
                    <div className="layout-flex-row--centered space-pre-l gap-l">
                        <Icon id="PlusPersonIcon" size="4xl" className="space-s" />
                        <div className="layout-flex-column">
                            <span className="text-bold">Want to add a partner?</span>
                            <a
                                onClick={() => {
                                    const paramBuilder = new URLSearchParams({
                                        edit: "true"
                                    });
                                    Object.keys(productState).forEach((product) => {
                                        const status = productState[
                                            product
                                        ] as IProductEditorState;

                                        if (status.isSelected) {
                                            paramBuilder.append("product", product);
                                        }
                                    });

                                    if (isFromDAP) {
                                        handleWarningDialogModal();
                                        setWarningNextStepUrl(
                                            `/family?${paramBuilder.toString()}`
                                        );
                                    } else {
                                        history.push(`/family?${paramBuilder.toString()}`);
                                    }
                                }}>
                                Edit who needs cover
                            </a>
                        </div>
                    </div>
                    <PromoteDAP isDapActive={isFromDAP} className={cx("layout-flex-row", "space-pre-l")}/>
                </React.Fragment>
            )}
        </div>
    );
};

export default QuoteSettingSidebar;
